import { useState, useEffect, useCallback, useRef } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { billingOpsAPI, soCreateAPI, vendorAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import SearchableSelect from '@/components/SearchableSelect';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, Trash, MagnifyingGlass, FloppyDisk, UploadSimple, FileText, Eye, DownloadSimple, PencilSimple,
} from '@phosphor-icons/react';
import { SerialHistoryLink } from '@/components/SerialHistoryLink';
import { AvailableSerialsForItem } from '@/components/AvailableSerialsForItem';
import ItemSearchPicker from '@/components/ItemSearchPicker';

export default function VendorBillsPage() {
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState('');
  const [branches, setBranches] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileRef = useRef(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportFrom, setExportFrom] = useState('');
  const [exportTo, setExportTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const runExport = async () => {
    setExporting(true);
    try {
      toast.info('Preparing export…');
      const res = await billingOpsAPI.exportVendorBills({ search, branch_id: branchFilter, from_date: exportFrom, to_date: exportTo });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      const suffix = (exportFrom || exportTo) ? `${exportFrom || 'start'}_to_${exportTo || 'end'}` : new Date().toISOString().slice(0, 10);
      a.download = `vendor_bills_${suffix}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch { toast.error('Export failed'); }
    finally { setExporting(false); }
  };

  // Create form
  const [form, setForm] = useState({
    vendor_name: '', branch_id: '', bill_number: '', bill_date: new Date().toISOString().split('T')[0], notes: '',
  });
  const [lineItems, setLineItems] = useState([
    { item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, serial_numbers: [], _serialInput: '' }
  ]);

  const loadBills = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await billingOpsAPI.listVendorBills({ page, search, branch_id: branchFilter });
      setBills(data.bills || []);
      setTotalPages(data.total_pages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, search, branchFilter]);

  useEffect(() => { loadBills(); }, [loadBills]);

  useEffect(() => {
    (async () => {
      const [brRes, itemsRes, venRes] = await Promise.allSettled([
        soCreateAPI.branches(),
        soCreateAPI.items({ per_page: 2000 }),
        vendorAPI.getAll(),
      ]);
      if (brRes.status === 'fulfilled') setBranches(brRes.value.data.branches || []);
      if (itemsRes.status === 'fulfilled') setCatalogItems(itemsRes.value.data.items || []);
      if (venRes.status === 'fulfilled') setVendors(venRes.value.data.vendors || []);
    })();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm({ vendor_name: '', branch_id: '', bill_number: '', bill_date: new Date().toISOString().split('T')[0], notes: '' });
    setLineItems([{ item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, serial_numbers: [], _serialInput: '' }]);
  };

  const handleEditBill = (bill) => {
    setEditingId(bill.id);
    setForm({
      vendor_id: bill.vendor_id || '', vendor_name: bill.vendor_name || '', branch_id: bill.branch_id || bill.location_id || '',
      bill_number: bill.bill_number || '', bill_date: bill.bill_date || new Date().toISOString().split('T')[0], notes: bill.notes || '',
    });
    setLineItems((bill.line_items || []).map(li => ({
      item_id: li.item_id || '', name: li.name || '', product_code: li.product_code || '', hsn_sac: li.hsn_sac || '',
      quantity: li.quantity || 1, rate: li.rate || 0, discount_percent: li.discount_percent || 0,
      discount_amount: li.discount_amount || 0, tax_percent: li.tax_percent || 0,
      serial_numbers: li.serial_numbers || [], _serialInput: (li.serial_numbers || []).join(', '),
    })) || [{ item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, serial_numbers: [], _serialInput: '' }]);
    setShowDetail(null);
    setShowCreate(true);
  };

  const selectCatalogItem = (idx, itemId) => {
    const item = catalogItems.find(i => i.id === itemId);
    if (!item) return;
    const gstRate = item.tax_preference === 'taxable' ? parseFloat(item.intra_state_gst || '18') : 0;
    setLineItems(prev => prev.map((li, i) => i === idx ? {
      ...li, item_id: item.id, name: item.name, product_code: item.product_code || '',
      hsn_sac: item.hsn_sac_code || '', rate: item.purchase_price || item.mrp || 0, tax_percent: gstRate,
    } : li));
  };

  const updateLineItem = (idx, field, value) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };

  const addLineItem = () => {
    setLineItems(prev => [...prev, { item_id: '', name: '', product_code: '', hsn_sac: '', quantity: 1, rate: 0, discount_percent: 0, discount_amount: 0, tax_percent: 0, serial_numbers: [], _serialInput: '' }]);
  };

  const removeLineItem = (idx) => {
    if (lineItems.length <= 1) return;
    setLineItems(prev => prev.filter((_, i) => i !== idx));
  };

  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const handleCreateBill = async () => {
    if (!form.vendor_name) { toast.error('Vendor name is required'); return; }
    if (!form.branch_id) { toast.error('Branch is required'); return; }
    if (!form.bill_number) { toast.error('Bill number is required'); return; }
    if (!lineItems.some(li => li.name)) { toast.error('At least one item is required'); return; }

    setSaving(true);
    try {
      const payload = {
        ...form,
        line_items: lineItems.filter(li => li.name).map(li => ({
          item_id: li.item_id, name: li.name, product_code: li.product_code,
          hsn_sac: li.hsn_sac, quantity: parseFloat(li.quantity) || 1,
          rate: parseFloat(li.rate) || 0,
          discount_percent: parseFloat(li.discount_percent) || 0,
          discount_amount: parseFloat(li.discount_amount) || 0,
          tax_percent: parseFloat(li.tax_percent) || 0,
          serial_numbers: li._serialInput ? li._serialInput.split(',').map(s => s.trim()).filter(Boolean) : [],
        })),
      };
      if (editingId) {
        await billingOpsAPI.updateVendorBill(editingId, payload);
        toast.success(`Vendor Bill ${form.bill_number} updated!`);
      } else {
        await billingOpsAPI.createVendorBill(payload);
        toast.success(`Vendor Bill ${form.bill_number} created!`);
      }
      setShowCreate(false);
      resetForm();
      loadBills();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${editingId ? 'update' : 'create'} vendor bill`);
    } finally { setSaving(false); }
  };

  const handleDeleteBill = async (bill) => {
    const confirmMsg = `Delete vendor bill "${bill.bill_number}"?\n\nThis will:\n- Decrement stock for all line items\n- Remove any received serials still available\n\nThis action cannot be undone. It will fail if any serial from this bill has already been sold or transferred.`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const { data } = await billingOpsAPI.deleteVendorBill(bill.id);
      toast.success(data?.message || 'Vendor bill deleted');
      loadBills();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vendor bill');
    }
  };

  const handlePDFUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Only PDF files are accepted');
      e.target.value = '';
      return;
    }
    try {
      toast.info('Uploading PDF...');
      await billingOpsAPI.uploadVendorBillPDF(file, branchFilter || '', 'Uploaded Vendor');
      toast.success('PDF uploaded! A new bill has been created for review.');
      loadBills();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      e.target.value = '';
    }
  };

  return (
    <div className="p-6" data-testid="vendor-bills-page">
      <PageHeader title="Vendor Bills" subtitle="Purchase bills, inventory receipts, and vendor invoices" />

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search bills..." className="pl-9" data-testid="vb-search" />
        </div>
        <SearchableSelect value={branchFilter || ''} onChange={v => { setBranchFilter(v); setPage(1); }}
          options={[{ value: '', label: 'All Branches' }, ...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]}
          placeholder="All Branches" testid="vb-branch-filter" buttonClassName="w-[200px]" />
        <label className="cursor-pointer">
          <input type="file" accept=".pdf" onChange={handlePDFUpload} className="hidden" data-testid="vb-upload-pdf-input" />
          <Button variant="outline" size="sm" asChild><span><UploadSimple size={14} className="mr-1" /> Upload PDF</span></Button>
        </label>
        <input type="file" ref={bulkFileRef} accept=".csv,.xlsx,.xls" className="hidden" data-testid="vb-bulk-upload-input"
          onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setBulkUploading(true);
            try {
              const { data } = await billingOpsAPI.bulkUploadVendorBills(f);
              toast.success(data.message || `Created ${data.created}`);
              if (data.errors?.length) toast.error(`${data.errors.length} issues: ${data.errors.slice(0, 3).join('; ')}`);
              loadBills();
            } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
            finally { setBulkUploading(false); e.target.value = ''; }
          }} />
        <Button variant="outline" size="sm" disabled={bulkUploading} onClick={() => bulkFileRef.current?.click()} data-testid="vb-bulk-upload-btn">
          <UploadSimple size={14} className="mr-1" /> {bulkUploading ? 'Uploading...' : 'Bulk Upload'}
        </Button>
        <Button variant="outline" size="sm" onClick={async () => {
          try {
            const res = await billingOpsAPI.vendorBillsTemplate();
            const url = URL.createObjectURL(res.data);
            const a = document.createElement('a'); a.href = url; a.download = 'vendor_bills_template.csv'; a.click(); URL.revokeObjectURL(url);
          } catch { toast.error('Template download failed'); }
        }} data-testid="vb-template-btn">
          <DownloadSimple size={14} className="mr-1" /> Template
        </Button>
        <Button variant="outline" size="sm" onClick={() => setExportOpen(true)} data-testid="vb-export-btn">
          <DownloadSimple size={14} className="mr-1" /> Export Excel
        </Button>
        {selectedIds.size > 0 && (
          <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="vb-bulk-delete-btn"
            onClick={async () => {
              if (!window.confirm(`Delete ${selectedIds.size} vendor bill(s)? Stock will be restored and serials removed.`)) return;
              try {
                const { data } = await billingOpsAPI.bulkDeleteVendorBills(Array.from(selectedIds));
                toast.success(`Deleted ${data.deleted}, removed ${data.serials_removed || 0} serial(s)`);
                setSelectedIds(new Set()); loadBills();
              } catch (e) { toast.error(e.response?.data?.detail || 'Bulk delete failed'); }
            }}>
            <Trash size={14} className="mr-1" /> Delete ({selectedIds.size})
          </Button>
        )}
        <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={() => { resetForm(); setShowCreate(true); }} data-testid="vb-create-btn">
          <Plus size={14} className="mr-1" /> New Vendor Bill
        </Button>
      </div>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-w-md" data-testid="vb-export-dialog">
          <DialogHeader><DialogTitle>Export Vendor Bills to Excel</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#64748B]">Optionally choose a bill-date range. Leave both blank to export all bills. Current search and branch filters are also applied.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">From date</label>
                <Input type="date" value={exportFrom} onChange={e => setExportFrom(e.target.value)} data-testid="vb-export-from" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">To date</label>
                <Input type="date" value={exportTo} onChange={e => setExportTo(e.target.value)} data-testid="vb-export-to" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => { setExportFrom(''); setExportTo(''); }} data-testid="vb-export-clear">Clear dates</Button>
              <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={runExport} disabled={exporting} data-testid="vb-export-download">
                <DownloadSimple size={14} className="mr-1" /> {exporting ? 'Exporting…' : 'Download Excel'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bills List */}
      <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="vb-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
              <th className="p-3 w-8">
                <input type="checkbox" checked={bills.length > 0 && selectedIds.size === bills.length}
                  onChange={() => setSelectedIds(prev => prev.size === bills.length ? new Set() : new Set(bills.map(b => b.id)))}
                  className="cursor-pointer" data-testid="vb-select-all" />
              </th>
              <th className="text-left p-3">Bill #</th>
              <th className="text-left p-3">Vendor</th>
              <th className="text-left p-3">Branch</th>
              <th className="text-left p-3">Date</th>
              <th className="text-right p-3">Grand Total</th>
              <th className="text-center p-3">Source</th>
              <th className="text-center p-3">Status</th>
              <th className="p-3 w-[100px]"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : bills.length === 0 ? (
              <tr><td colSpan={9} className="text-center py-8 text-[#64748B]">No vendor bills found</td></tr>
            ) : bills.map(b => (
              <tr key={b.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`vb-row-${b.id}`}>
                <td className="p-3">
                  <input type="checkbox" checked={selectedIds.has(b.id)} onChange={() => setSelectedIds(prev => {
                    const n = new Set(prev); if (n.has(b.id)) n.delete(b.id); else n.add(b.id); return n;
                  })} className="cursor-pointer" data-testid={`vb-select-${b.id}`} />
                </td>
                <td className="p-3 font-medium text-[#0F4C81]">{b.bill_number}</td>
                <td className="p-3">{b.vendor_name}</td>
                <td className="p-3 text-xs">{b.branch_name}</td>
                <td className="p-3 text-xs">{b.bill_date}</td>
                <td className="p-3 text-right font-medium">{fmt(b.grand_total)}</td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${b.source === 'manual' ? 'bg-blue-50 text-blue-700' : b.source === 'pdf_upload' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                    {b.source || 'manual'}
                  </span>
                </td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${b.status === 'received' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                    {b.status}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1 justify-end">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setShowDetail(b)} data-testid={`vb-view-${b.id}`}>
                      <Eye size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                      onClick={() => handleDeleteBill(b)}
                      data-testid={`vb-delete-${b.id}`}
                      title="Delete vendor bill"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-3 border-t border-[#E2E8F0]">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <span className="text-xs text-[#64748B]">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={18} /> Vendor Bill: {showDetail?.bill_number}
            </DialogTitle>
          </DialogHeader>
          {showDetail && (
            <div className="space-y-4 text-sm">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={() => handleEditBill(showDetail)} className="text-blue-600 border-blue-200 hover:bg-blue-50" data-testid="vb-edit-btn">
                  <PencilSimple size={14} className="mr-1" /> Edit Bill
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div><span className="text-[#64748B]">Vendor:</span> <strong>{showDetail.vendor_name}</strong></div>
                <div><span className="text-[#64748B]">Branch:</span> {showDetail.branch_name}</div>
                <div><span className="text-[#64748B]">Date:</span> {showDetail.bill_date}</div>
                <div><span className="text-[#64748B]">Grand Total:</span> <strong>{fmt(showDetail.grand_total)}</strong></div>
                <div><span className="text-[#64748B]">Source:</span> {showDetail.source}</div>
                <div><span className="text-[#64748B]">Status:</span> {showDetail.status}</div>
              </div>
              {showDetail.line_items?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-[#64748B] mb-2">Line Items</h4>
                  <table className="w-full text-xs">
                    <thead><tr className="bg-[#F8FAFC]">
                      <th className="text-left p-2">Item</th><th className="text-left p-2">HSN</th>
                      <th className="text-center p-2">Qty</th><th className="text-right p-2">Rate</th>
                      <th className="text-right p-2">Tax</th><th className="text-right p-2">Total</th>
                      <th className="text-left p-2">Serial #s</th>
                    </tr></thead>
                    <tbody>
                      {showDetail.line_items.map((li, i) => (
                        <tr key={i} className="border-t border-[#E2E8F0]">
                          <td className="p-2">{li.name}</td>
                          <td className="p-2">{li.hsn_sac || '-'}</td>
                          <td className="p-2 text-center">{li.quantity}</td>
                          <td className="p-2 text-right">{fmt(li.rate)}</td>
                          <td className="p-2 text-right">{li.tax_percent || 0}%</td>
                          <td className="p-2 text-right font-medium">{fmt(li.total)}</td>
                          <td className="p-2 text-[10px]">
                            {(li.serial_numbers || []).length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {li.serial_numbers.map(sn => (
                                  <SerialHistoryLink key={sn} serialNumber={sn} className="bg-[#E8F5E9] text-[#0F4C81]" />
                                ))}
                              </div>
                            ) : (
                              <AvailableSerialsForItem
                                itemId={li.item_id}
                                clinicId={showDetail.branch_id || showDetail.clinic_id}
                                warehouseName={showDetail.branch_name || showDetail.clinic_name}
                                qty={li.quantity}
                              />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {showDetail.notes && <p className="text-xs text-[#64748B]"><strong>Notes:</strong> {showDetail.notes}</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={(o) => { setShowCreate(o); if (!o) resetForm(); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Vendor Bill' : 'Create Vendor Bill'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Vendor *</label>
                <SearchableSelect value={form.vendor_name} onChange={v => setForm(f => ({ ...f, vendor_name: v }))} placeholder="Select vendor..." testid="vb-vendor-name" buttonClassName="w-full"
                  options={[...vendors.map(v => ({ value: v.name, label: v.display_name || v.name }))]} />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Branch *</label>
                <SearchableSelect value={form.branch_id} onChange={v => setForm(f => ({ ...f, branch_id: v }))} placeholder="Select branch..." testid="vb-branch" buttonClassName="w-full"
                  options={[...branches.map(b => ({ value: b.id, label: b.name, keywords: b.code || b.clinic_code || "" }))]} />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Bill Number *</label>
                <Input value={form.bill_number} onChange={e => setForm(f => ({ ...f, bill_number: e.target.value }))} placeholder="e.g. VB-001" data-testid="vb-bill-number" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Bill Date</label>
                <Input type="date" value={form.bill_date} onChange={e => setForm(f => ({ ...f, bill_date: e.target.value }))} data-testid="vb-bill-date" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-[#64748B] block mb-1">Notes</label>
                <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="Additional notes" data-testid="vb-notes" />
              </div>
            </div>

            <h4 className="text-xs font-semibold text-[#0F172A]">Line Items</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="vb-items-table">
                <thead>
                  <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
                    <th className="text-left p-2 w-[200px]">Item</th>
                    <th className="text-center p-2 w-[50px]">Qty</th>
                    <th className="text-right p-2 w-[80px]">Rate</th>
                    <th className="text-center p-2 w-[50px]">Tax%</th>
                    <th className="text-left p-2 w-[140px]">Serial #s</th>
                    <th className="p-2 w-[36px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((li, idx) => (
                    <tr key={idx} className="border-t border-[#E2E8F0]" data-testid={`vb-create-line-${idx}`}>
                      <td className="p-2">
                        <ItemSearchPicker items={catalogItems} value={li.name} onPick={(item) => selectCatalogItem(idx, item.id)}
                          testid={`vb-item-select-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" min="1" value={li.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} className="text-center h-8 text-xs" data-testid={`vb-qty-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.rate} onChange={e => updateLineItem(idx, 'rate', e.target.value)} className="text-right h-8 text-xs" data-testid={`vb-rate-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input type="number" value={li.tax_percent} onChange={e => updateLineItem(idx, 'tax_percent', e.target.value)} className="text-center h-8 text-xs" data-testid={`vb-tax-${idx}`} />
                      </td>
                      <td className="p-2">
                        <Input value={li._serialInput} onChange={e => updateLineItem(idx, '_serialInput', e.target.value)} placeholder="SN1, SN2" className="h-8 text-xs" data-testid={`vb-serial-${idx}`} />
                      </td>
                      <td className="p-2">
                        {lineItems.length > 1 && (
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => removeLineItem(idx)}>
                            <Trash size={14} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button variant="outline" size="sm" onClick={addLineItem} data-testid="vb-add-item">
              <Plus size={14} className="mr-1" /> Add Item
            </Button>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreateBill} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="vb-submit-btn">
                <FloppyDisk size={16} className="mr-2" />{saving ? (editingId ? 'Updating...' : 'Creating...') : (editingId ? 'Update Vendor Bill' : 'Create Vendor Bill')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

